import React, { useState, useEffect, useRef, useContext } from "react";
import { searchAddress } from "./geocode";
import { UserContext } from "../App";

// Google Places results never shown (kept in sync with functions/utils/geocode.js).
// "Vestibule Paris" (furniture store, bd de Sébastopol) was confused with the
// Air France "Le Vestibule" lounge at CDG 2E below.
const BLOCKED_PLACE_IDS = new Set(["ChIJBVd3bRpu5kcRBitHlhPjWNs"]);
const BLOCKED_PLACE_PATTERNS = [/^(?=.*vestibule)(?=.*s[ée]bastopol)/i];
const isBlockedPlace = (s) =>
  (s.place_id && BLOCKED_PLACE_IDS.has(s.place_id)) ||
  BLOCKED_PLACE_PATTERNS.some((re) => re.test(`${s.display_name || ""} ${s.formatted_address || ""}`));

// Predefined addresses for frequent locations with GPS coordinates
const predefinedAddresses = [
  // === CHARLES DE GAULLE (CDG) ===
  {
    display_name: "Le Vestibule La Première, CDG Terminal 2E Porte 17 – Départs",
    keywords: ["aéroport", "airport", "le", "la", "vestibule", "premiere", "première", "af", "air", "france", "cdg", "roissy", "paris"],
    coordinates: {
      lat: 49.003781,
      lon: 2.577597
    }
  },
  {
    display_name: "Aéroport Charles de Gaulle (Roissy CDG)",
    keywords: ["aéroport", "airport", "cdg", "charles", "gaulle", "terminal", "1", "roissy"],
    coordinates: {
      lat: 49.0097,
      lon: 2.5479
    }
  },

  // === ORLY ===
  {
    display_name: "Aéroport d'Orly",
    keywords: ["aéroport", "airport", "orly"],
    coordinates: {
      lat: 48.729534,
      lon: 2.368176
    }
  },

  // === LE BOURGET ===
  {
    display_name: "Aéroport du Bourget",
    keywords: ["aéroport", "airport", "bourget", "le bourget"],
    coordinates: {
      lat: 48.9694,
      lon: 2.4414
    }
  },

  // === NICE CÔTE D'AZUR ===
  {
    display_name: "Aéroport Nice Côte d'Azur",
    keywords: ["aéroport", "airport", "nice", "côte", "azur"],
    coordinates: {
      lat: 43.664451,
      lon: 7.203781
    }
  },

  // === MARSEILLE PROVENCE ===
  {
    display_name: "Aéroport Marseille Provence",
    keywords: ["aéroport", "airport", "marseille", "provence"],
    coordinates: {
      lat: 43.442815,
      lon: 5.223483
    }
  }
];


const CustomPlacesAutocomplete = ({ setSelection, value, disabled = false }) => {
  const { userInfos } = useContext(UserContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const debounceTime = 700; // Increased from 500ms to reduce API calls
  const timeoutId = useRef(null);
  const cacheRef = useRef(new Map()); // Cache for API results

  // Function to filter and score predefined addresses
  function filterPredefinedAddresses(input) {
    if (!input || input.length < 2) return [];
    
    const searchLower = input.toLowerCase().trim();
    const searchTerms = searchLower.split(' ').filter(term => term.length > 0);
    
    // Filter and score each address
    const scoredAddresses = predefinedAddresses
      .map(address => {
        const displayLower = address.display_name.toLowerCase();
        let score = 0;
        let matchedTerms = 0;
        
        // Check each search term
        searchTerms.forEach(term => {
          let termMatched = false;
          
          // Exact match in display name = highest score
          if (displayLower === term) {
            score += 1000;
            termMatched = true;
          }
          // Display name starts with term = very high score
          else if (displayLower.startsWith(term)) {
            score += 500;
            termMatched = true;
          }
          // Display name contains term = high score
          else if (displayLower.includes(term)) {
            score += 100;
            termMatched = true;
          }
          // Check keywords
          else {
            const keywordMatch = address.keywords.some(keyword => {
              const keywordLower = keyword.toLowerCase();
              if (keywordLower === term) {
                score += 50;
                return true;
              } else if (keywordLower.includes(term) || term.includes(keywordLower)) {
                score += 10;
                return true;
              }
              return false;
            });
            if (keywordMatch) termMatched = true;
          }
          
          if (termMatched) matchedTerms++;
        });
        
        // ALL terms must match - if not, return score 0
        if (matchedTerms !== searchTerms.length) {
          score = 0;
        } else {
          // Bonus if all terms matched
          score += 200;
        }
        
        return { address, score, matchedTerms };
      })
      .filter(item => item.score > 0 && item.matchedTerms === searchTerms.length) // Only keep addresses where ALL terms matched
      .sort((a, b) => b.score - a.score) // Sort by score descending
      .map(item => item.address); // Extract just the address
    
    return scoredAddresses;
  }

  async function fetchSuggestions(input) {
    setLoading(true);

    try {
      // Get matching predefined addresses
      const predefinedMatches = filterPredefinedAddresses(input);
      
      // Normalize input for cache key
      const cacheKey = input.toLowerCase().trim();
      
      // Check cache first
      let apiResults = [];
      if (cacheRef.current.has(cacheKey)) {
        // Use cached results - no API call!
        apiResults = cacheRef.current.get(cacheKey);
      } else {
        // Get API results and cache them
        apiResults = await searchAddress(userInfos.jwt, input);
        cacheRef.current.set(cacheKey, apiResults);
        
        // Limit cache size to 50 entries (prevent memory bloat)
        if (cacheRef.current.size > 50) {
          const firstKey = cacheRef.current.keys().next().value;
          cacheRef.current.delete(firstKey);
        }
      }
      
      // Combine results: predefined addresses first, then API (limit to 5 total)
      const combinedResults = [...predefinedMatches, ...apiResults.filter((s) => !isBlockedPlace(s))].slice(0, 5);
      
      setSuggestions(combinedResults);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      // In case of API error, show at least predefined addresses
      const predefinedMatches = filterPredefinedAddresses(input);
      setSuggestions(predefinedMatches.slice(0, 5));
    }

    setLoading(false);
  }

  function debounceFetchSuggestions(input) {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }

    timeoutId.current = setTimeout(() => {
      fetchSuggestions(input);
    }, debounceTime);
  }

  const handleInputChange = (event) => {
    const input = event.target.value;
    setSearchTerm(input);
    if (input.length > 2) {
      debounceFetchSuggestions(input);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.display_name);
    setSuggestions([]);

    // If this is a predefined address with coordinates, pass them directly (no API call needed!)
    if (suggestion.coordinates) {
      setSelection({
        ...suggestion,
        geocoding_address: suggestion.display_name,
        lat: suggestion.coordinates.lat,
        lon: suggestion.coordinates.lon
      });
      return;
    }

    // For Google Places API results, pass the place_id for accurate geocoding
    setSelection({
      ...suggestion,
      geocoding_address: suggestion.formatted_address || suggestion.display_name,
      place_id: suggestion.place_id
    });
  };

  useEffect(() => {
    if (value) {
      setSearchTerm(value); // Update input field with selected pickup address
    }
  }, [value]);

  return (
    <div>
      <input
        type="text"
        placeholder="Search Places ..."
        className="form-control location-search-input"
        value={searchTerm}
        onChange={handleInputChange}
        disabled={disabled}
      />
      <div className="autocomplete-dropdown-container kt-widget4">
        {loading && <div>Chargement...</div>}
        {suggestions.map((suggestion, index) => {
          const isPredefined = suggestion.keywords !== undefined;
          return (
            <div
              key={index}
              className={`suggestion-item kt-widget4__item ${isPredefined ? 'predefined-address' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
                style={{
                  borderLeft: isPredefined ? '3px solid #6D00E7' : 'none',
                  paddingLeft: isPredefined ? '12px' : '8px'
                }}
            >
              <span className="kt-widget4__title">
                {suggestion.display_name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomPlacesAutocomplete;
